import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import Pagination from '@material-ui/lab/Pagination';
import { Box, debounce, FormControl, Grid, makeStyles, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SearchIcon from '@material-ui/icons/Search';
import { useAppDispatch, useAppSelector } from '../../../store';
import { clearClient, searchClientsAsync } from '../../../store/adviser';
import { OnboardDelivery, ProductFilterOptions, ProgressFilterOptions, SearchClientsSortOptions } from '../../../api/apiClients';
import { Colors, FormLabel } from '../../controls/helpers';
import { TextInput } from '../../controls/inputs';
import { useSharedStyles } from '../../../common/sharedStyles';
import { adviserProductRoles, ClientSearchParams, supportRoles } from '../../../common/types';
import ClientRow from './ClientRow';
import { getRoles } from '../Helpers';

const initialSearchState: ClientSearchParams = {
    filterByProducts: ProductFilterOptions.None,
    filterByProgress: ProgressFilterOptions.None,
    page: 1,
    query: '',
    sortOrder: SearchClientsSortOptions.CreatedDesc
};

const progressFilters = [
    { value: ProgressFilterOptions.None, label: <>&nbsp;</> },
    { value: ProgressFilterOptions.NotStarted, label: 'Not started' },
    { value: ProgressFilterOptions.InProgress, label: 'In progress' },
    { value: ProgressFilterOptions.RequiresAction, label: 'Requires action' },
    { value: ProgressFilterOptions.Complete, label: 'Complete' },
    { value: ProgressFilterOptions.Archived, label: 'Archived' },
    { value: ProgressFilterOptions.Retention, label: 'Retention' }
];

const productFilters = [
    { value: ProductFilterOptions.None, label: <>&nbsp;</> },
    { value: ProductFilterOptions.KiwiSaver, label: 'KiwiSaver' },
    { value: ProductFilterOptions.WealthBuilder, label: 'Wealth Builder' },
    { value: ProductFilterOptions.IncomeGenerator, label: 'Income Generator' }
];

export default ({ history }: RouteComponentProps) => {
    const { user } = useAppSelector(state => state.oidc);
    const { client, searchResults } = useAppSelector(state => state.adviser);
    const [clientSearchParams, setClientSearchParams] = useState<ClientSearchParams>(initialSearchState);
    const dispatch = useAppDispatch();
    const classes = useStyles();
    const roles = getRoles(user);
    const isSupportRole = !roles.some(role => adviserProductRoles.includes(role)) && roles.some(role => supportRoles.includes(role));

    const searchClients = debounce((query: string) => {
        setClientSearchParams({ ...clientSearchParams, query });
    }, 999);

    const handlePageChange = (_: ChangeEvent<unknown>, page: number) => {
        setClientSearchParams({ ...clientSearchParams, page });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
     
    const sort = (sortOrder: SearchClientsSortOptions) => {
        setClientSearchParams({ ...clientSearchParams, sortOrder });
    }
     
    const filterProgress = (filterByProgress: ProgressFilterOptions | ProductFilterOptions) => {
        setClientSearchParams({ ...clientSearchParams, filterByProgress: filterByProgress as ProgressFilterOptions, page: 1 });
    }

    const filterProduct = (filterByProducts: ProgressFilterOptions | ProductFilterOptions) => {
        setClientSearchParams({ ...clientSearchParams, filterByProducts: filterByProducts as ProductFilterOptions, page: 1 });
    }

    const clearFilters = () => {
        setClientSearchParams({ ...initialSearchState, query: clientSearchParams.query, sortOrder: clientSearchParams.sortOrder });
    }

    useEffect(() => {
        dispatch(clearClient());
    }, []);

    useEffect(() => {
        dispatch(searchClientsAsync(clientSearchParams));
    }, [clientSearchParams]);

    useEffect(() => {
        if (client) {
            if (client.kiwiSaverApplication?.isRetention)
                history.push('/adviser/kiwisaver-retention-tool');
            else if (client.onboardDelivery === OnboardDelivery.BrowserKiwiSaverOnly && !!client.kiwiSaverApplication && !client.wealthBuilderApplications?.length && !client.wealthBuilderApplications?.length)
                history.push('/adviser/new-application/kiwisaver');
            else
                history.push('/adviser/new-application/confirm-details');
        }
    }, [client]);

    return <Box my={2}>
        <Grid container>
            <Grid item xs={12} style={{ padding: '0 90px 20px' }}>
                <Grid container spacing={3} alignItems="flex-end">
                    <Grid item md={3} xs={6}>
                        <SimpleSelect value={clientSearchParams.filterByProgress} placeholder="Progress" options={progressFilters} onFilterChange={filterProgress} />
                    </Grid>
                    <Grid item md={3} xs={6}>
                        <SimpleSelect value={clientSearchParams.filterByProducts} placeholder="Product" options={productFilters} onFilterChange={filterProduct} />
                    </Grid>
                    <Grid item md xs={12}>
                        <FormLabel label="Search" />
                        <TextInput onChange={searchClients} endAdornment={<SearchIcon />} />
                    </Grid>
                    <Grid item style={{ marginBottom: 14 }}>
                        <a href="#" style={{ fontWeight: 500, textDecoration: 'none' }} onClick={e => { e.preventDefault(); clearFilters(); return false; }}>Clear filters</a>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Table className={classes.table}>
                    <TableHead className={classes.tableHeader}>
                        <TableRow>
                            <TableCell width="4%" />
                            <TableCell width="2%" />
                            <TableCell width="15%" onClick={() => sort(clientSearchParams.sortOrder === SearchClientsSortOptions.FirstName ? SearchClientsSortOptions.FirstNameDesc : SearchClientsSortOptions.FirstName)}>
                                <TableSortLabel active={clientSearchParams.sortOrder === SearchClientsSortOptions.FirstName || clientSearchParams.sortOrder === SearchClientsSortOptions.FirstNameDesc} IconComponent={ArrowDropDownIcon} direction={clientSearchParams.sortOrder.endsWith('Desc') ? 'desc' : 'asc'}>Name</TableSortLabel>
                            </TableCell>
                            <TableCell width="15%" onClick={() => sort(clientSearchParams.sortOrder === SearchClientsSortOptions.Adviser ? SearchClientsSortOptions.AdviserDesc : SearchClientsSortOptions.Adviser)}>
                                <TableSortLabel active={clientSearchParams.sortOrder === SearchClientsSortOptions.Adviser || clientSearchParams.sortOrder === SearchClientsSortOptions.AdviserDesc} IconComponent={ArrowDropDownIcon} direction={clientSearchParams.sortOrder.endsWith('Desc') ? 'desc' : 'asc'}>Adviser</TableSortLabel>
                            </TableCell>
                            <TableCell width="9%" onClick={() => sort(clientSearchParams.sortOrder === SearchClientsSortOptions.Created ? SearchClientsSortOptions.CreatedDesc : SearchClientsSortOptions.Created)}>
                                <TableSortLabel active={clientSearchParams.sortOrder === SearchClientsSortOptions.Created || clientSearchParams.sortOrder === SearchClientsSortOptions.CreatedDesc} IconComponent={ArrowDropDownIcon} direction={clientSearchParams.sortOrder.endsWith('Desc') ? 'desc' : 'asc'}>Created</TableSortLabel>
                            </TableCell>
                            <TableCell width="2%" align="center">Sent</TableCell>
                            <TableCell width="11%">Products</TableCell>
                            <TableCell width="31%">Progress</TableCell>
                            <TableCell width="7%" />
                            <TableCell width="4%" />
                        </TableRow>
                    </TableHead>
                    <TableBody className={classes.body}>
                        <TableRow style={{ height: 20 }} />
                            {searchResults?.results?.map(client => <ClientRow key={client.clientId} client={client} isSupportRole={isSupportRole} refreshGrid={() => setClientSearchParams({ ...clientSearchParams })} />)}
                        <TableRow style={{ height: 20 }} />
                    </TableBody>
                </Table>
            </Grid>
            <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" pt={1.5} pr={7}>
                    <Pagination count={searchResults?.pageCount || 0} page={clientSearchParams.page} onChange={handlePageChange} size="large" color="primary" shape="rounded" />
                </Box>
            </Grid>
        </Grid>
    </Box>
}

const SimpleSelect = ({ options: optionsIn, value, placeholder, onFilterChange }: {
    options: Array<{ label: ReactNode; value: ProductFilterOptions | ProgressFilterOptions; }>;
    value: ProductFilterOptions | ProgressFilterOptions;
    placeholder?: string;
    onFilterChange: (value: ProductFilterOptions | ProgressFilterOptions) => void;
}) => {
    const sharedClasses = useSharedStyles();
    return <FormControl fullWidth>
        <FormLabel label={placeholder} />
        <Select
            fullWidth
            value={value}
            variant="outlined"
            className={sharedClasses.input}
            IconComponent={props => <ExpandMoreIcon {...props} style={{ color: Colors.Black, marginRight: 5 }} />}
            onChange={event => onFilterChange(event.target.value as ProductFilterOptions | ProgressFilterOptions)}>
            {optionsIn.map(({ value, label }) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
        </Select>
    </FormControl>
}

const useStyles = makeStyles({
    table: {
        minWidth: 650,
        backgroundColor: Colors.LightGrey,
        borderBottom: `1px solid ${Colors.Grey}`
    },
    body: {
        color: Colors.Black,
        '& td': {
            padding: '7px 10px'
        }
    },
    tableHeader: {
        '& th': {
            fontSize: 14,
            backgroundColor: Colors.Blue,
            color: Colors.White,
            padding: 10,
            '& span': {
                color: `${Colors.White} !important`,
                '& > svg': {
                    color: `${Colors.White} !important`,
                    width: 24,
                    height: 24
                }
            }
        }
    }
}, { name: 'OnboardingStatus' });
